import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import { TableColumnsDropdown } from '@rio-cloud/rio-uikit/Table';
import TableToolbar, { TableToolbarColumn } from '@rio-cloud/rio-uikit/TableToolbar';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { useMemo } from 'react';
import { type IntlShape, useIntl } from 'react-intl';

import { DatePeriodFilter } from '#components/shared/DatePeriodFilter.tsx';
import { Filter, getOption } from '#components/shared/Filter.tsx';
import { FilterResetButton } from '#components/shared/FilterResetButton.tsx';
import { OrderStatusData } from '#components/shared/OrderStatusData.ts';
import { SearchInput } from '#components/shared/SearchInput.tsx';
import { useFeatureToggle } from '#configuration/featureFlags/useFeatureToggle.ts';
import type { OrderType } from '#domain/Order.ts';
import type { DatePeriodFilterState } from '#hooks/shared/useDatePeriodFilter.ts';
import { useFeatureParams } from '#hooks/shared/useFeatureParams.ts';
import type { OrderWithSortableKey } from '#utils/orders.util.ts';

import { CreateOrderButton } from './CreateOrderButton.tsx';
import type { OrderTableColumnConfig } from './OrderTable.tsx';
import { PlanOrdersButton } from './PlanOrdersButton.tsx';

const getOptions = (intlShape: IntlShape) => [
    getOption('open', OrderStatusData, intlShape),
    getOption('planned', OrderStatusData, intlShape),
    getOption('in-transit', OrderStatusData, intlShape),
    getOption('loading', OrderStatusData, intlShape),
    getOption('unloading', OrderStatusData, intlShape),
    getOption('executing', OrderStatusData, intlShape),
    getOption('finished', OrderStatusData, intlShape),
];

const getTypeOptions = (intl: IntlShape) => [
    {
        id: 'transport',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.order.type.transport',
        }),
        icon: <span className='uikit-icon icon-truck' />,
        selected: true,
    },
    {
        id: 'service',
        label: intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.order.type.service',
        }),
        icon: <span className='uikit-icon icon-wrench' />,
        selected: true,
    },
];

type OrderTableToolbarProps = {
    selectedOrders: OrderWithSortableKey[];
    onOpenCreateTourDialog: () => void;
    onOpenCreateOrderDialog: (type?: OrderType) => void;
    onOpenCsvImportDialog: () => void;
    searchInput: string;
    onSearch: (value: string) => void;
    statusFilter: string[];
    onFilterStatus: (values: string[]) => void;
    onResetFilters: () => void;
    typeFilter?: string[];
    onFilterType?: (values: string[]) => void;
    dateFilter: DatePeriodFilterState;
    columns: OrderTableColumnConfig[];
    onColumnOrderChange: (columnOrder: string[]) => void;
    onColumnVisibilityChange: (columnKey: string, visible: boolean) => void;
    onResetColumns: () => void;
    onViewTypeChange: (viewType: TableViewTogglesViewType) => void;
    viewType: TableViewTogglesViewType;
};

export const OrderTableToolbar = ({
    selectedOrders,
    onOpenCreateTourDialog,
    onOpenCreateOrderDialog,
    onOpenCsvImportDialog,
    searchInput,
    onSearch,
    statusFilter,
    onFilterStatus,
    onResetFilters,
    typeFilter = ['transport', 'service'],
    onFilterType = () => undefined,
    dateFilter,
    columns,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onResetColumns,
    onViewTypeChange,
    viewType,
}: OrderTableToolbarProps) => {
    const intl = useIntl();
    const options = useMemo(() => getOptions(intl), [intl.locale]);
    const typeOptions = useMemo(() => getTypeOptions(intl), [intl.locale]);
    const orderSelected = selectedOrders.length > 0;
    const { csvImportEnabled } = useFeatureParams();
    const showCsvImport = useFeatureToggle('showCsvImport', false);
    const showCsvImportButton = csvImportEnabled || showCsvImport;
    const createOrderTitle = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.title',
    });
    const hasActiveFilter = statusFilter.length > 0 || typeFilter.length !== 2 || dateFilter.hasActiveFilter;

    return (
        <TableToolbar>
            <TableToolbarColumn>
                <ButtonToolbar>
                    <CreateOrderButton
                        id='create-order-dropdown'
                        title={createOrderTitle}
                        bsStyle={orderSelected ? Button.DEFAULT : Button.PRIMARY}
                        onSelect={onOpenCreateOrderDialog}
                    />
                    {showCsvImportButton && (
                        <Button
                            bsStyle={Button.DEFAULT}
                            iconName='upload'
                            autoTrackingKey='intl-msg:ordercom-cloud-frontend.orders.csv-import.action'
                            onClick={onOpenCsvImportDialog}
                        >
                            {intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.orders.csv-import.action',
                            })}
                        </Button>
                    )}
                    {orderSelected && (
                        <PlanOrdersButton onOpenTourDialog={onOpenCreateTourDialog} selectedOrders={selectedOrders} />
                    )}
                </ButtonToolbar>
            </TableToolbarColumn>

            <TableToolbarColumn horizontalAlign='right' separator={hasActiveFilter}>
                <FilterResetButton
                    hasActiveFilter={statusFilter.length > 0 || typeFilter.length !== 2 || dateFilter.hasActiveFilter}
                    onResetFilter={onResetFilters}
                    trackingKey='ordercom.orders.table-toolbar.reset-filter'
                />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right' separator>
                <DatePeriodFilter {...dateFilter} compact />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right' separator>
                <Filter values={statusFilter} onFilter={onFilterStatus} options={options} />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right' separator>
                <Filter values={typeFilter} onFilter={onFilterType} options={typeOptions} />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right' separator>
                <SearchInput value={searchInput} onSearch={onSearch} />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right'>
                <TableViewToggles
                    viewType={viewType}
                    onViewTypeChange={onViewTypeChange}
                    tableViewLabel={intl.formatMessage({
                        id: 'intl-msg:common-message.table.viewToggle.table',
                    })}
                    singleCardViewLabel={intl.formatMessage({
                        id: 'intl-msg:common-message.table.viewToggle.list',
                    })}
                    multiCardsViewLabel={intl.formatMessage({
                        id: 'intl-msg:common-message.table.viewToggle.cards',
                    })}
                />
            </TableToolbarColumn>
            <TableToolbarColumn horizontalAlign='right'>
                <TableColumnsDropdown
                    columns={columns}
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.table.columns.label',
                    })}
                    onColumnOrderChange={onColumnOrderChange}
                    onColumnVisibilityChange={onColumnVisibilityChange}
                    onReset={onResetColumns}
                    showResetButton
                    resetLabel={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.table.columns.reset',
                    })}
                />
            </TableToolbarColumn>
        </TableToolbar>
    );
};
